// frontend/src/components/Diario/TimelineHistorico.jsx
// Timeline vertical para histórico do plantão atual

import React, { useMemo, useState } from 'react';
import { Clock, User, Heart, Pill, ShowerHead, Activity, UtensilsCrossed, FileText, Move3D, AlertTriangle, CircleCheckBig } from 'lucide-react';
import { getUsuarioFotoUrl } from '../../utils/imageUtils';

/**
 * Componente de Avatar com fallback para ícone
 */
const AvatarImage = ({ src, alt, fallbackIcon, fallbackColor }) => {
    const [imageError, setImageError] = useState(false);

    if (imageError) {
        return (
            <div className={`w-12 h-12 rounded-full ${fallbackColor} flex items-center justify-center`}>
                {fallbackIcon}
            </div>
        );
    }

    return (
        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200 flex items-center justify-center bg-white">
            <img 
                src={src} 
                alt={alt} 
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
            />
        </div>
    );
};

/**
 * Mapeia tipo de registro para ícone
 */
const getTipoIcon = (tipo) => {
    const tipoLower = tipo?.toLowerCase() || '';
    if (tipoLower.includes('sinais vitais')) return { Icon: Heart, color: 'text-red-500 bg-red-100' };
    if (tipoLower.includes('atividade fisica') || tipoLower.includes('atividade física')) return { Icon: Activity, color: 'text-green-500 bg-green-100' };
    if (tipoLower.includes('medicacao') || tipoLower.includes('medicação')) return { Icon: Pill, color: 'text-blue-500 bg-blue-100' };
    if (tipoLower.includes('higien') || tipoLower.includes('higiene')) return { Icon: ShowerHead, color: 'text-indigo-500 bg-indigo-100' };
    if (tipoLower.includes('alimentacao') || tipoLower.includes('alimentação')) return { Icon: UtensilsCrossed, color: 'text-amber-500 bg-amber-100' };
    if (tipoLower.includes('posicionamento')) return { Icon: Move3D, color: 'text-teal-500 bg-teal-100' };
    if (tipoLower.includes('observacao') || tipoLower.includes('observação')) return { Icon: FileText, color: 'text-purple-500 bg-purple-100' };
    if (tipoLower.includes('tarefa atrasada')) return { Icon: AlertTriangle, color: 'text-red-500 bg-red-100' };
    return { Icon: FileText, color: 'text-gray-500 bg-gray-100' };
};

/**
 * Renderiza conteúdo detalhado do registro
 */
const renderConteudoDetalhado = (conteudo, tipo) => {
    if (!conteudo) return null;

    let dados;
    try {
        dados = typeof conteudo === 'string' ? JSON.parse(conteudo) : conteudo;
    } catch {
        return (
            <p className="text-sm text-gray-600 mt-1">
                {String(conteudo).replace(/[{}"]/g, '').trim()}
            </p>
        );
    }

    if (typeof dados === 'object' && !Array.isArray(dados)) {
        const tipoLower = tipo?.toLowerCase() || '';
        
        // Sinais Vitais - Grid compacto
        if (tipoLower.includes('sinais vitais')) {
            const campos = Object.entries(dados).slice(0, 4); // Mostra apenas 4 primeiros
            return (
                <div className="flex flex-wrap gap-2 mt-2">
                    {campos.map(([chave, valor]) => (
                        <span
                            key={chave}
                            className="px-2 py-1 bg-gray-50 rounded text-xs text-gray-700"
                        >
                            <span className="font-medium">{chave.replace(/_/g, ' ')}:</span> {valor}
                        </span>
                    ))}
                </div>
            );
        }

        // Outros tipos - Texto simples
        const texto = Object.entries(dados)
            .map(([chave, valor]) => `${chave.replace(/_/g, ' ')}: ${valor}`)
            .join(' | ');
        
        return (
            <p className="text-sm text-gray-600 mt-1">{texto}</p>
        );
    }

    return (
        <p className="text-sm text-gray-600 mt-1">
            {String(dados).replace(/[{}"]/g, '').trim()}
        </p>
    );
};

/**
 * Componente de Timeline para histórico
 * 
 * @param {array} registros - Lista de registros
 * @param {array} tarefasAtrasadas - Lista de tarefas atrasadas
 * @param {array} tarefasConcluidas - Lista de tarefas concluídas (opcional)
 */
const TimelineHistorico = ({ registros = [], tarefasAtrasadas = [], tarefasConcluidas = [] }) => {
    // Converte tarefas atrasadas em registros virtuais
    const registrosAtrasados = useMemo(() => {
        return tarefasAtrasadas.map(tarefa => {
            const horaAgendada = tarefa.hora_agendada 
                ? new Date(tarefa.hora_agendada).toLocaleString("pt-BR")
                : "Horário não informado";
            
            return {
                id: `tarefa-atrasada-${tarefa.id}`,
                tipo_registro: "⚠️ Tarefa Atrasada",
                data_hora_registro: tarefa.hora_agendada || new Date().toISOString(),
                observacoes: `Tarefa "${tarefa.descricao || tarefa.tipo_tarefa}" estava agendada para ${horaAgendada}.`,
                conteudo_detalhado: {
                    tipo_tarefa: tarefa.tipo_tarefa,
                    descricao: tarefa.descricao || tarefa.tipo_tarefa,
                    hora_agendada: horaAgendada,
                    status: tarefa.status || "Atrasada"
                },
                profissional_nome: "Higeia IA",
                isTarefaAtrasada: true
            };
        });
    }, [tarefasAtrasadas]);

    // Função auxiliar para extrair hora_agendada de um registro ou tarefa
    const extrairHoraAgendada = (item) => {
        if (!item) return null;
        
        // Tenta extrair de diferentes locais possíveis
        if (item.hora_agendada) {
            // Se já é um objeto Date ou string ISO válida
            const date = new Date(item.hora_agendada);
            if (!isNaN(date.getTime())) return date.getTime();
        }
        
        // Tenta do conteudo_detalhado (pode ser string formatada ou objeto)
        if (item.conteudo_detalhado) {
            let conteudo;
            try {
                conteudo = typeof item.conteudo_detalhado === 'string' 
                    ? JSON.parse(item.conteudo_detalhado) 
                    : item.conteudo_detalhado;
            } catch (e) {
                // Se falhar o parse, tenta usar como string direta
                conteudo = item.conteudo_detalhado;
            }
            
            if (conteudo?.hora_agendada) {
                // Pode ser string formatada "28/11/2025, 21:30:00" ou ISO
                const date = new Date(conteudo.hora_agendada);
                if (!isNaN(date.getTime())) return date.getTime();
            }
        }
        
        return null;
    };

    // Função auxiliar para obter data de ordenação (prioriza data_hora_registro para registros manuais)
    const obterDataOrdenacao = (item) => {
        // PRIORIDADE 1: data_hora_registro (para registros manuais)
        if (item.data_hora_registro) {
            const dataRegistro = new Date(item.data_hora_registro).getTime();
            if (!isNaN(dataRegistro)) return dataRegistro;
        }
        
        // PRIORIDADE 2: hora_conclusao (para tarefas concluídas - hora real de conclusão)
        if (item.hora_conclusao) {
            const horaConclusao = new Date(item.hora_conclusao).getTime();
            if (!isNaN(horaConclusao)) return horaConclusao;
        }
        
        // PRIORIDADE 3: hora_criacao
        if (item.hora_criacao) {
            const dataCriacao = new Date(item.hora_criacao).getTime();
            if (!isNaN(dataCriacao)) return dataCriacao;
        }
        
        // PRIORIDADE 4: hora_agendada (para tarefas agendadas/concluídas sem hora de conclusão)
        const horaAgendada = extrairHoraAgendada(item);
        if (horaAgendada !== null) return horaAgendada;
        
        return 0;
    };

    // Converte tarefas concluídas em registros virtuais
    const registrosTarefasConcluidas = useMemo(() => {
        return tarefasConcluidas.map(tarefa => {
            const horaAgendada = tarefa.hora_agendada 
                ? new Date(tarefa.hora_agendada).toLocaleString("pt-BR")
                : "Horário não informado";
            
            // PRIORIDADE para ordenação: hora_conclusao > hora_criacao > hora_agendada
            // Isso garante que tarefas concluídas sejam ordenadas pela hora real de conclusão
            let dataOrdenacao;
            if (tarefa.hora_conclusao) {
                dataOrdenacao = new Date(tarefa.hora_conclusao).toISOString();
            } else if (tarefa.hora_criacao) {
                dataOrdenacao = new Date(tarefa.hora_criacao).toISOString();
            } else if (tarefa.data_conclusao) {
                dataOrdenacao = new Date(tarefa.data_conclusao).toISOString();
            } else if (tarefa.hora_agendada) {
                dataOrdenacao = new Date(tarefa.hora_agendada).toISOString();
            } else {
                dataOrdenacao = new Date().toISOString();
            }
            
            return {
                id: `tarefa-concluida-${tarefa.id}`,
                tipo_registro: tarefa.tipo_tarefa || 'Tarefa Concluída',
                data_hora_registro: dataOrdenacao,
                observacoes: tarefa.descricao || tarefa.observacoes_conclusao || `Tarefa "${tarefa.tipo_tarefa}" concluída.`,
                conteudo_detalhado: {
                    tipo_tarefa: tarefa.tipo_tarefa,
                    descricao: tarefa.descricao || tarefa.tipo_tarefa,
                    hora_agendada: horaAgendada,
                    status: 'Concluída'
                },
                profissional_nome: tarefa.usuario_nome || tarefa.cuidador_nome || "Higeia IA",
                profissional_foto_url: tarefa.usuario_foto_url || tarefa.cuidador_foto_url || null,
                isTarefaConcluida: true,
                agenda_tarefa_id: tarefa.id, // Armazena o ID da tarefa para relacionamento
                hora_criacao: tarefa.hora_criacao, // Preserva hora_criacao para uso na ordenação
                hora_conclusao: tarefa.hora_conclusao, // Preserva hora_conclusao para uso na ordenação
                hora_agendada: tarefa.hora_agendada // Preserva hora_agendada original para ordenação
            };
        });
    }, [tarefasConcluidas]);

    // Combina e ordena registros
    // Lógica ROBUSTA: Registros de diário devem aparecer ANTES das tarefas concluídas relacionadas
    const todosRegistros = useMemo(() => {
        // Normaliza IDs para comparação (garante que sejam números, compara de forma flexível)
        const normalizeId = (id) => {
            if (id == null || id === undefined) return null;
            // Tenta converter para número primeiro
            const num = Number(id);
            if (!isNaN(num) && num > 0) return num;
            // Se não for número válido, tenta usar como string (caso seja um ID especial)
            if (String(id).trim() !== '') return String(id).trim();
            return null;
        };
        
        // Cria um mapa de registros por agenda_tarefa_id para encontrar registros relacionados
        // Usa chave normalizada para garantir que IDs diferentes mas equivalentes sejam agrupados
        const registrosPorTarefaId = new Map();
        registros.forEach(registro => {
            const tarefaId = normalizeId(registro.agenda_tarefa_id);
            if (tarefaId !== null) {
                // Usa o ID normalizado como chave
                const chave = String(tarefaId);
                if (!registrosPorTarefaId.has(chave)) {
                    registrosPorTarefaId.set(chave, []);
                }
                registrosPorTarefaId.get(chave).push(registro);
            }
        });
        
        // Cria um mapa de tarefas concluídas por agenda_tarefa_id (usando ID normalizado como chave)
        // IMPORTANTE: agenda_tarefa_id na tarefa concluída deve ser o mesmo ID usado nos registros
        const tarefasConcluidasPorId = new Map();
        registrosTarefasConcluidas.forEach(tarefa => {
            // O campo agenda_tarefa_id na tarefa concluída contém o ID da tarefa original
            const tarefaId = normalizeId(tarefa.agenda_tarefa_id);
            if (tarefaId !== null) {
                const chave = String(tarefaId);
                tarefasConcluidasPorId.set(chave, tarefa);
            }
        });
        
        // ========== ESTRATÉGIA: AGRUPAMENTO E ORDENAÇÃO ==========
        // 1. PRIMEIRO: Processa registros e cria grupos com suas tarefas relacionadas
        // 2. DEPOIS: Processa tarefas concluídas que não foram agrupadas
        // 3. FINALMENTE: Processa itens sem relacionamento (tarefas atrasadas, etc)
        // 4. Dentro de cada grupo, registros vêm PRIMEIRO, depois a tarefa concluída
        
        const grupos = [];
        const itensProcessados = new Set();
        
        // ETAPA 1: Processa registros primeiro (eles criam grupos com tarefas relacionadas)
        registros.forEach(registro => {
            if (itensProcessados.has(registro.id)) {
                return;
            }
            
            const registroTarefaId = normalizeId(registro.agenda_tarefa_id);
            
            // Se tem tarefa concluída relacionada, cria grupo
            if (registroTarefaId !== null) {
                const chaveTarefa = String(registroTarefaId);
                const tarefaConcluida = tarefasConcluidasPorId.get(chaveTarefa);
                
                if (tarefaConcluida && !itensProcessados.has(tarefaConcluida.id)) {
                    // Busca TODOS os registros relacionados a esta tarefa
                    const todosRegistrosRelacionados = registrosPorTarefaId.get(chaveTarefa) || [];
                    
                    // Calcula data do grupo: prioriza datas dos registros sobre a tarefa
                    const datasRegistros = todosRegistrosRelacionados
                        .map(r => obterDataOrdenacao(r))
                        .filter(d => !isNaN(d) && d > 0);
                    const dataTarefa = obterDataOrdenacao(tarefaConcluida);
                    
                    // Se há registros, usa a data mais recente dos registros; senão usa a data da tarefa
                    const dataGrupo = datasRegistros.length > 0 
                        ? Math.max(...datasRegistros)
                        : (!isNaN(dataTarefa) && dataTarefa > 0 ? dataTarefa : Date.now());
                    
                    const grupo = {
                        tipo: 'tarefa_com_registros',
                        tarefa: tarefaConcluida,
                        registros: todosRegistrosRelacionados,
                        dataGrupo: dataGrupo
                    };
                    
                    grupos.push(grupo);
                    itensProcessados.add(tarefaConcluida.id);
                    todosRegistrosRelacionados.forEach(r => itensProcessados.add(r.id));
                    return;
                }
            }
            
            // Se não tem tarefa relacionada, cria grupo individual
            const grupo = {
                tipo: 'item_individual',
                item: registro,
                dataGrupo: obterDataOrdenacao(registro)
            };
            grupos.push(grupo);
            itensProcessados.add(registro.id);
        });
        
        // ETAPA 2: Processa tarefas concluídas que não foram agrupadas
        registrosTarefasConcluidas.forEach(tarefa => {
            if (itensProcessados.has(tarefa.id)) {
                return;
            }
            
            const tarefaId = normalizeId(tarefa.agenda_tarefa_id);
            const chaveTarefa = tarefaId !== null ? String(tarefaId) : null;
            const registrosRelacionados = chaveTarefa !== null ? (registrosPorTarefaId.get(chaveTarefa) || []) : [];
            
            // Se tem registros relacionados que não foram processados ainda (não deveria acontecer, mas garante)
            const registrosNaoProcessados = registrosRelacionados.filter(r => !itensProcessados.has(r.id));
            
            // Calcula data do grupo: prioriza datas dos registros sobre a tarefa
            const datasRegistros = registrosNaoProcessados
                .map(r => obterDataOrdenacao(r))
                .filter(d => !isNaN(d) && d > 0);
            const dataTarefa = obterDataOrdenacao(tarefa);
            
            if (registrosNaoProcessados.length > 0) {
                // Cria grupo com registros não processados
                // Se há registros, usa a data mais recente dos registros; senão usa a data da tarefa
                const dataGrupo = datasRegistros.length > 0 
                    ? Math.max(...datasRegistros)
                    : (!isNaN(dataTarefa) && dataTarefa > 0 ? dataTarefa : Date.now());
                
                const grupo = {
                    tipo: 'tarefa_com_registros',
                    tarefa: tarefa,
                    registros: registrosNaoProcessados,
                    dataGrupo: dataGrupo
                };
                grupos.push(grupo);
                itensProcessados.add(tarefa.id);
                registrosNaoProcessados.forEach(r => itensProcessados.add(r.id));
            } else {
                // Tarefa sem registros relacionados (ou todos já foram processados)
                const grupo = {
                    tipo: 'item_individual',
                    item: tarefa,
                    dataGrupo: obterDataOrdenacao(tarefa)
                };
                grupos.push(grupo);
                itensProcessados.add(tarefa.id);
            }
        });
        
        // ETAPA 3: Processa tarefas atrasadas (não têm relacionamento com registros)
        registrosAtrasados.forEach(tarefaAtrasada => {
            if (itensProcessados.has(tarefaAtrasada.id)) {
                return;
            }
            
            const grupo = {
                tipo: 'item_individual',
                item: tarefaAtrasada,
                dataGrupo: obterDataOrdenacao(tarefaAtrasada)
            };
            grupos.push(grupo);
            itensProcessados.add(tarefaAtrasada.id);
        });
        
        // Ordena grupos pela data (decrescente - mais recente primeiro)
        // Se duas datas forem iguais, prioriza registros sobre tarefas
        grupos.sort((a, b) => {
            const diffData = b.dataGrupo - a.dataGrupo;
            if (Math.abs(diffData) < 1000) { // Se a diferença for menor que 1 segundo, considera igual
                // Prioriza grupos com registros sobre tarefas individuais
                if (a.tipo === 'tarefa_com_registros' && b.tipo === 'item_individual') {
                    return -1; // a vem antes de b
                }
                if (a.tipo === 'item_individual' && b.tipo === 'tarefa_com_registros') {
                    return 1; // b vem antes de a
                }
                // Se ambos são itens individuais, prioriza registros sobre tarefas concluídas
                if (a.tipo === 'item_individual' && b.tipo === 'item_individual') {
                    const aIsTarefa = a.item?.isTarefaConcluida || false;
                    const bIsTarefa = b.item?.isTarefaConcluida || false;
                    if (!aIsTarefa && bIsTarefa) {
                        return -1; // Registro vem antes de tarefa
                    }
                    if (aIsTarefa && !bIsTarefa) {
                        return 1; // Tarefa vem depois de registro
                    }
                }
            }
            return diffData;
        });
        
        // Desmonta grupos em lista linear, mantendo ordem dentro de cada grupo
        const resultado = [];
        grupos.forEach(grupo => {
            if (grupo.tipo === 'tarefa_com_registros') {
                // Para registros criados através de tarefas: REGISTRO PRIMEIRO, depois TAREFA
                // Ordena registros por data (mais recentes primeiro)
                const registrosOrdenados = [...grupo.registros].sort((a, b) => {
                    const dateA = obterDataOrdenacao(a);
                    const dateB = obterDataOrdenacao(b);
                    return dateB - dateA; // Decrescente (mais recente primeiro)
                });
                // Adiciona registros PRIMEIRO
                resultado.push(...registrosOrdenados);
                // Depois adiciona a tarefa concluída
                resultado.push(grupo.tarefa);
            } else {
                // Item individual
                resultado.push(grupo.item);
            }
        });
        
        return resultado;
    }, [registros, registrosAtrasados, registrosTarefasConcluidas]);

    if (todosRegistros.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <Clock size={48} className="mx-auto mb-4 text-gray-400 opacity-50" />
                <p className="font-semibold">Nenhum registro no histórico do plantão atual.</p>
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Linha vertical da timeline */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

            {/* Itens da timeline */}
            <div className="space-y-6">
                {todosRegistros.map((registro, index) => {
                    const { Icon, color } = getTipoIcon(registro.tipo_registro);
                    const dataHora = registro.data_hora_registro
                        ? new Date(registro.data_hora_registro)
                        : null;
                    const horaFormatada = dataHora
                        ? dataHora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                        : '--:--';
                    const dataFormatada = dataHora
                        ? dataHora.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                        : '';

                    // Determinar se deve mostrar imagem ou ícone
                    const isHigeiaIA = registro.profissional_nome === 'Higeia IA';
                    const hasUserPhoto = registro.profissional_foto_url;
                    const showImage = isHigeiaIA || hasUserPhoto;

                    return (
                        <div
                            key={registro.id}
                            className={`relative flex gap-4 ${
                                registro.isTarefaAtrasada ? 'bg-red-50 p-4 rounded-lg border-l-4 border-red-400' : ''
                            }`}
                        >
                            {/* Círculo da timeline - Imagem ou Ícone */}
                            <div className="relative z-10 flex-shrink-0">
                                {showImage ? (
                                    <AvatarImage 
                                        src={isHigeiaIA ? '/images/perfil_ai.png' : getUsuarioFotoUrl(registro.profissional_foto_url)} 
                                        alt={isHigeiaIA ? 'Higeia IA' : (registro.profissional_nome || 'Usuário')}
                                        fallbackIcon={<Icon size={20} />}
                                        fallbackColor={color}
                                    />
                                ) : (
                                    <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center`}>
                                        <Icon size={20} />
                                    </div>
                                )}
                            </div>

                            {/* Conteúdo */}
                            <div className="flex-1 min-w-0 pb-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            {registro.isTarefaConcluida && (
                                                <CircleCheckBig size={18} className="text-green-600" />
                                            )}
                                            {(registro.profissional_nome && registro.profissional_nome !== 'Higeia IA') && (
                                                <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center flex-shrink-0`}>
                                                    <Icon size={16} />
                                                </div>
                                            )}
                                            <h4 className={`font-semibold ${
                                                registro.isTarefaAtrasada ? 'text-red-800' : 'text-gray-800'
                                            }`}>
                                                {registro.tipo_registro}
                                            </h4>
                                        </div>

                                        {registro.observacoes && (
                                            <p className={`text-sm italic mb-2 ${
                                                registro.isTarefaAtrasada ? 'text-red-700' : 'text-gray-600'
                                            }`}>
                                                "{registro.observacoes}"
                                            </p>
                                        )}

                                        {renderConteudoDetalhado(registro.conteudo_detalhado, registro.tipo_registro)}
                                    </div>

                                    {/* Informações laterais */}
                                    <div className="flex-shrink-0 text-right pr-3">
                                        <div className="flex items-center justify-end gap-1 text-xs text-gray-500 mb-1">
                                            <Clock size={12} />
                                            <span>{horaFormatada}</span>
                                        </div>
                                        {dataFormatada && (
                                            <div className="text-xs text-gray-400 mb-2">
                                                {dataFormatada}
                                            </div>
                                        )}
                                        <div className="flex items-center justify-end gap-1 text-xs text-gray-500">
                                            <User size={12} />
                                            <span className="truncate max-w-[100px]">
                                                {registro.profissional_nome || 'Higeia IA'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TimelineHistorico;

